import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, Search, Bell, Plus, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SidebarContent } from "@/components/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { initials } from "@/lib/format";
import { ROLE_LABEL } from "@/lib/constants";

export function Topbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [count, setCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (active) setCount(data.count);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => { active = false; clearInterval(t); };
  }, [location.pathname]);

  const crumbs = location.pathname.split("/").filter(Boolean);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/leads?q=${encodeURIComponent(q.trim())}`);
  };

  const canAddLead = ["super_admin", "manager", "field_executive", "sales_partner"].includes(user?.role);

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-slate-200 bg-white/85 backdrop-blur-md">
      <div className="flex h-full items-center gap-3 px-4 lg:px-6">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden grid place-items-center h-11 w-11 -ml-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64 border-0">
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        <nav className="hidden md:flex items-center text-sm text-slate-500 min-w-0">
          <Link to="/dashboard" className="hover:text-teal font-medium">Home</Link>
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center min-w-0">
              <ChevronRight className="h-4 w-4 mx-1 text-slate-300 shrink-0" />
              <span className="capitalize truncate text-slate-700 font-medium" data-testid={`breadcrumb-${i}`}>
                {/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(c) || /^[a-z]-\d+$/i.test(c) ? "Details" : c.replace(/-/g, " ").slice(0, 24)}
              </span>
            </span>
          ))}
        </nav>

        <form onSubmit={onSearch} className="ml-auto hidden sm:block relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search leads…"
            data-testid="global-search-input"
            className="h-10 w-48 lg:w-72 rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm focus:ring-2 focus:ring-teal focus:border-teal outline-none"
          />
        </form>

        {canAddLead && (
          <button
            onClick={() => navigate("/leads/new")}
            data-testid="topbar-add-lead-btn"
            className="hidden sm:inline-flex items-center gap-2 h-10 rounded-lg bg-teal px-3.5 text-sm font-semibold text-white hover:bg-teal-700 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Lead
          </button>
        )}

        <button
          onClick={() => navigate("/notifications")}
          data-testid="topbar-notifications-btn"
          className="relative grid place-items-center h-11 w-11 rounded-lg hover:bg-slate-100"
        >
          <Bell className="h-5 w-5 text-slate-600" />
          {count > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-destructive text-white text-[10px] font-bold">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-lg hover:bg-slate-100 p-1 pr-2" data-testid="user-menu-btn">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.profile_photo} />
                <AvatarFallback className="bg-navy text-white text-xs">{initials(user?.full_name)}</AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left leading-tight">
                <div className="text-sm font-semibold text-slate-800 max-w-[140px] truncate">{user?.full_name}</div>
                <div className="text-[11px] text-slate-500">{ROLE_LABEL[user?.role] || user?.role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-semibold">{user?.full_name}</div>
              <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">Profile & Security</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="menu-notifications">Notifications</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout" className="text-destructive">
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
